import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage, Form, Formik } from "formik";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { register } from "../../actions/authAction";
import TextError from "../../components/WraapperComponent/TextError";
import LeagalDisclaimerPopup from "./LegalDisclaimerPopup";
import SubscriptionInfoPopup from "./SubscriptionInfoPopup";
import useQuery from "./useQuery";
import SchoolFormPopup from "./SchoolFormPopup";
import { statesList } from "../../constants/states";

const StudentForm = ({ setShowSuccessSection, setUser }) => {
  const dispatch = useDispatch();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const formikRef = useRef();
  const [showSchoolModal, setShowSchoolModal] = useState(null);
  const schoolList = useSelector((state) => state.settings.schools);
  let query = useQuery();
  let data = query.get("query");
  let queryData = null;
  if (data) {
    queryData = JSON.parse(atob(decodeURIComponent(data)));
  }

  const initialValues = {
    prefix: queryData ? queryData.prefix : "",
    first_name: queryData ? queryData.first_name : "",
    last_name: queryData ? queryData.last_name : "",
    user_email: queryData ? queryData.user_email : "",
    user_name: queryData ? queryData.user_name : "",
    phone: queryData ? queryData.phone : "",
    address: queryData ? queryData.address : "",
    city: queryData ? queryData.city : "",
    state: queryData ? queryData.state : "",
    zip: queryData ? queryData.zip : "",
    password: "",
    confirm_password: "",
    school: queryData ? queryData.school : "",
    referred_by_id: queryData ? queryData.referral_code : "",
    disclaimer: false,
  };

  const validationSchema = Yup.object({
    prefix: Yup.string().nullable().required("This field is required"),
    first_name: Yup.string().nullable().required("This field is required"),
    last_name: Yup.string().nullable().required("This field is required"),
    phone: Yup.string()
      .nullable()
      .min(10, "Phone number must be 10 digits long")
      .max(10, "Phone number must be 10 digits long")
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        "Phone number is not valid"
      )
      .required("This field is required"),
    address: Yup.string().nullable().required("This field is required"),
    city: Yup.string().nullable().required("This field is required"),
    state: Yup.string().nullable().required("This field is required"),
    zip: Yup.string()
      .nullable()
      .max(5)
      .min(5)
      .matches(/^\d{5}$/, "Zip code is not valid")
      .required("This field is required"),
    user_email: Yup.string()
      .nullable()
      .matches(
        /^(?!.*@)/,
        "Enter the part of your email before the @ and then choose the school from the dropdown"
      )
      .required("This field is required"),
    user_name: Yup.string()
      .nullable()
      .matches(
        /^[a-zA-Z0-9_.-]*$/,
        "Only letters and numbers, 2 to 20 characters"
      )
      .min(2, "Only letters and numbers, 2 to 64 characters")
      .max(64, "Only letters and numbers, 2 to 64 characters")
      .required("This field is required"),
    password: Yup.string()
      .nullable()
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]*$/,
        "Password must contain a minimum 8 characters with at least 1 number and 1 letter"
      )
      .min(
        8,
        "Password must contain a minimum 8 characters with at least 1 number and 1 letter"
      )
      .required("This field is required"),
    confirm_password: Yup.string()
      .nullable()
      .oneOf([Yup.ref("password"), null], "Confirm password do not matched")
      .required("This field is required"),
    referred_by_id: Yup.string().nullable(),
    school: Yup.string().nullable().required("This field is required"),
    disclaimer: Yup.boolean()
      .oneOf([true], "You must accept the disclaimer to proceed.")
      .required("You must accept the disclaimer to proceed."),
  });

  const getSchoolName = (value) => {
    let schoolName = "";
    if (value == 1) {
      schoolName = "@emperors.edu";
    } else if (value == 2) {
      schoolName = "@pacificcollege.edu";
    } else if (value == 3) {
      schoolName = "@yosan.edu";
    } else if (value == 4) {
      schoolName = "@achs.edu";
    }
    return schoolName;
  };

  const onSubmit = (values, { resetForm }) => {
    let payload = {
      params: {
        action: "registerAsNewStudent",
        data: {
          prefix: { value: values.prefix },
          first_name: values.first_name?.trim(),
          last_name: values.last_name?.trim(),
          user_email: values.user_email?.trim(),
          phone: values.phone,
          user_name: values.user_name?.trim(),
          password: values.password,
          referral_code: values.referred_by_id,
          school: values.school,
          address: values.address,
          city: values.city,
          state: values.state,
          zip: values.zip,
          subscription_data: subscriptionData
            ? {
                ccName: subscriptionData.nameOncard,
                ccNumber: subscriptionData.cardNumber,
                ccExpMonth: subscriptionData.expirationMonth,
                ccExpYear: subscriptionData.expirationYear,
                expiration:
                  subscriptionData.expirationMonth +
                  subscriptionData.expirationYear,
                first_name: subscriptionData.firstname?.trim(),
                last_name: subscriptionData.lastname?.trim(),
                email: subscriptionData.email?.trim(),
                address_1: subscriptionData.address_1,
                address_2: subscriptionData.address_2,
                city: subscriptionData.city,
                state: subscriptionData.state,
                phone: subscriptionData.phone,
                zip: subscriptionData.zip,
                subscription_id: subscriptionData.subscription_id,
              }
            : null,
        },
      },
    };

    dispatch(
      register(payload, () => {
        setUser({
          prefix: values.prefix,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.user_email + values.school,
        });
        setShowSuccessSection(true);
        resetForm();
      })
    );
  };

  const handleExternalChange = (value) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("disclaimer", value); // Or false, depending on your logic
    }
  };

  return (
    <div className="row">
      <div className="col-md-6">
        <h5 className="title">Student Registration Form</h5>
        {showDisclaimer && (
          <LeagalDisclaimerPopup
            student
            closePopup={() => setShowDisclaimer(false)}
            handleAgree={setAcceptDisclaimer}
            handleExternalChange={handleExternalChange}
          />
        )}
        {showSubscriptionPopup && (
          <SubscriptionInfoPopup
            practitioner
            closePopup={() => setShowSubscriptionPopup(false)}
            roleId={4}
            setSubscriptionData={setSubscriptionData}
          />
        )}
        {showSchoolModal && (
          <SchoolFormPopup closePopup={() => setShowSchoolModal(false)} />
        )}
        <div className="form-section">
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isValid,
              dirty,
              setFieldValue,
            }) => {
              return (
                <Form>
                  <div className="row">
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.prefix && touched.prefix ? "hasError" : null
                        }`}
                      >
                        <select
                          name="prefix"
                          defaultValue=""
                          value={values.prefix}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="" disabled>
                            SELECT PREFIX
                          </option>
                          <option value="Mr">Mr</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Ms">Ms</option>
                          <option value="Dr">Dr</option>
                          <option value="Prof">Prof</option>
                        </select>
                      </div>
                      <ErrorMessage name="prefix" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.first_name && touched.first_name
                            ? "hasError"
                            : null
                        }`}
                      >
                        <input
                          type="text"
                          value={values.first_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="first_name"
                          placeholder="First Name*"
                        />
                      </div>
                      <ErrorMessage name="first_name" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.last_name && touched.last_name
                            ? "hasError"
                            : null
                        }`}
                      >
                        <input
                          type="text"
                          value={values.last_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="last_name"
                          placeholder="Last Name*"
                        />
                      </div>
                      <ErrorMessage name="last_name" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div className="school-line">
                        <div>
                          <div
                            className={`form-wrapper ${
                              errors.user_email && touched.user_email
                                ? "hasError"
                                : null
                            }`}
                          >
                            <input
                              type="text"
                              value={values.user_email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              name="user_email"
                              placeholder="School email before @*"
                            />
                          </div>
                        </div>
                        <label>@</label>
                        <div>
                          <div
                            className={`form-wrapper ${
                              errors.school && touched.school
                                ? "hasError"
                                : null
                            }`}
                          >
                            <select
                              name="school"
                              defaultValue=""
                              value={values.school}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="" disabled>
                                SELECT SCHOOL
                              </option>
                              {schoolList.map((x) => {
                                return (
                                  <option value={`@${x.domain}`}>
                                    {x.domain}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-sm-6">
                          {" "}
                          <ErrorMessage
                            name="user_email"
                            component={TextError}
                          />
                        </div>
                        <div
                          className="col-sm-6"
                          style={{ paddingLeft: "35px" }}
                        >
                          <ErrorMessage name="school" component={TextError} />
                        </div>
                      </div>
                      <p
                        className="link"
                        onClick={() => setShowSchoolModal(true)}
                      >
                        If your school/institution is not listed please click
                        here to request that it be added.
                      </p>
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.phone && touched.phone ? "hasError" : null
                        }`}
                      >
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="\d*"
                          value={values.phone}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, "");
                            setFieldValue("phone", val);
                          }}
                          onBlur={handleBlur}
                          name="phone"
                          maxLength={10}
                          placeholder="Phone Number*"
                          onPaste={(e) => {
                            const str = e.clipboardData.getData("Text");
                            let newStr = str.replace(/[^0-9]/g, "");
                            if (newStr.length > 10 && newStr[0] == 1) {
                              newStr = newStr.slice(1);
                            }
                            if (str !== newStr) {
                              e.preventDefault();
                              setFieldValue("phone", newStr);
                            }
                          }}
                        />
                      </div>
                      <ErrorMessage name="phone" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.address && touched.address ? "hasError" : null
                        }`}
                      >
                        <input
                          type="text"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="address"
                          placeholder="Street*"
                        />
                      </div>
                      <ErrorMessage name="address" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.city && touched.city ? "hasError" : null
                        }`}
                      >
                        <input
                          type="text"
                          value={values.city}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="city"
                          placeholder="City*"
                        />
                      </div>
                      <ErrorMessage name="city" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.state && touched.state ? "hasError" : null
                        }`}
                      >
                        <select
                          value={values.state}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="state"
                          placeholder="State*"
                        >
                          <option value="" disabled>
                            SELECT STATE*
                          </option>

                          {statesList.map((option) => {
                            return (
                              <option key={option.value} value={option.value}>
                                {option.key}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <ErrorMessage name="state" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.zip && touched.zip ? "hasError" : null
                        }`}
                      >
                        <input
                          type="text"
                          value={values.zip}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          maxLength={5}
                          name="zip"
                          placeholder="Zip*"
                        />
                      </div>
                      <ErrorMessage name="zip" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${
                          errors.user_name && touched.user_name
                            ? "hasError"
                            : null
                        }`}
                      >
                        <input
                          type="text"
                          value={values.user_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="user_name"
                          placeholder="Username*"
                        />
                      </div>
                      <ErrorMessage name="user_name" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper password-field ${
                          errors.password && touched.password
                            ? "hasError"
                            : null
                        }`}
                      >
                        <input
                          type={showPassword ? "text" : "password"}
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="password"
                          placeholder="Password*"
                        />
                        <div className="pass-icon">
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            color="gray"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        </div>
                      </div>
                      <ErrorMessage name="password" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper password-field ${
                          errors.confirm_password && touched.confirm_password
                            ? "hasError"
                            : null
                        }`}
                      >
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={values.confirm_password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="confirm_password"
                          placeholder="Repeat Password*"
                        />
                        <div className="pass-icon">
                          <FontAwesomeIcon
                            icon={showConfirmPassword ? faEyeSlash : faEye}
                            color="gray"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          />
                        </div>
                      </div>
                      <ErrorMessage
                        name="confirm_password"
                        component={TextError}
                      />
                    </div>
                    {/* <div className="col-sm-12">
                      <div className="form-wrapper">
                        <input
                          type="text"
                          value={values.referred_by_id}
                          onChange={handleChange}
                          name="referred_by_id"
                          placeholder="Referral Code"
                        />
                      </div>
                      <p className="small">
                        If you have been given a special Referral Code you can
                        add it here in order to give credit to the person who
                        referred you to us.
                      </p>
                    </div> */}
                    {/* <div className="col-sm-12">
                      <div
                        className="link"
                        onClick={() => setShowSubscriptionPopup(true)}
                      >
                        <p>Add Subscription</p>
                      </div>
                    </div> */}
                    <div className="col-sm-12 mt-1">
                      <label
                        style={{ marginLeft: 0 }}
                        className="container"
                        onClick={() => setShowDisclaimer(true)}
                      >
                        LEGAL DISCLAIMERS
                        <input type="checkbox" checked={acceptDisclaimer} />
                        <span className="checkmark"></span>
                      </label>{" "}
                      <ErrorMessage name="disclaimer" component={TextError} />
                    </div>
                  </div>
                  <div className="submit-section mt-2">
                    <button className="btn-orange" type="submit">
                      Register
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
      <div className="col-md-6">
        <h5 className="title">Student Benefits Include:</h5>
        <ul>
          <li>Commission on All Herb Orders</li>
          <li>Prescription Builder</li>
          <li>Formula Writing</li>
          <li>Conversion Calculator</li>
          <li>Patient and Order Management</li>
          <li>Suggest Dosages</li>
          <li>Order Fulfillment</li>
        </ul>
      </div>
    </div>
  );
};
export default StudentForm;
